import { trackAnalyticsEvent } from "@/lib/analytics"
import { useCallback, useEffect } from "react"
import { useAffiliationDetection } from "./use-affiliation-detection"
import { useConfigParams } from "./use-config-params"

export function usePageTracking(
  pageName: string,
  previousPageName?: string,
  trackPage = true,
) {
  const pageAnalyticsFn = usePageAnalyticsFn(pageName, previousPageName)
  const pagePreRegAnalyticsFn = usePagePreRegAnalyticsFn(
    pageName,
    previousPageName,
  )

  const isAffiliationFlow = useAffiliationDetection()

  useEffect(() => {
    if (!trackPage) return

    if (isAffiliationFlow) {
      pagePreRegAnalyticsFn()
    } else {
      pageAnalyticsFn()
    }
  }, [isAffiliationFlow, pageAnalyticsFn, pagePreRegAnalyticsFn, trackPage])
}

function usePageAnalyticsFn(pageName: string, previousPageName?: string) {
  const pageAnalyticsFn = usePageAnalyticsCommon(pageName, previousPageName)
  return pageAnalyticsFn
}

// This additional payload must be a stable reference, otherwise the callback
// will be re-created at each render.
// See https://react.dev/reference/react/useCallback#parameters
const affiliationAdditionalPayload = { flusso_registrazione: "pdv" }
function usePagePreRegAnalyticsFn(pageName: string, previousPageName?: string) {
  const pageAnalyticsFn = usePageAnalyticsCommon(
    pageName,
    previousPageName,
    affiliationAdditionalPayload,
  )
  return pageAnalyticsFn
}

function usePageAnalyticsCommon(
  pageName: string,
  previousPageName?: string,
  additionalPayload?: { [key: string]: string },
) {
  const { channel } = useConfigParams()

  const pageAnalyticsFn = useCallback(() => {
    trackAnalyticsEvent({
      loaded_page: "yes",
      page_name: pageName,
      conversion_pagename: pageName,
      previous_pagename: previousPageName ?? "",
      section: "",
      subsection1: "",
      subsection2: "",
      login_status: "not-logged",
      site_id: channel.toString(),
      url: window.location.hostname + window.location.pathname,
      url_querystring: window.location.search,
      intcmp: "",
      operating_system: window.navigator.userAgent,
      ...additionalPayload,
    })
  }, [additionalPayload, channel, pageName, previousPageName])

  return pageAnalyticsFn
}
